import { CATALOGUE_POLICES, policesDeLaCharte } from '../../contract/index.js';
/** `@font-face` des polices de la charte, auto-hébergées (D12). */
function fontFaces(charte) {
    return policesDeLaCharte(charte)
        .flatMap((nom) => {
        const definition = CATALOGUE_POLICES[nom];
        return definition.fichiers.map((fichier) => `@font-face {
  font-family: "${definition.famille}";
  src: url("${fichier.chemin}") format("woff2");
  font-weight: ${String(fichier.graisse)};
  font-style: ${fichier.style};
  font-display: swap;
}`);
    })
        .join('\n');
}
function pile(nom) {
    const definition = CATALOGUE_POLICES[nom];
    return `"${definition.famille}", ${definition.repli}`;
}
/**
 * Feuille de style du thème **Accrochage**, d'après `designs/c-accrochage.html` :
 * l'accrochage au mur — image d'abord, grilles pleines, plein écran, blanc,
 * peu de texte. Pour l'art contemporain et la photographie. Les grilles
 * d'œuvres exploitent le `srcset` des variantes existantes (320/800/1600/2400,
 * AVIF puis WebP) ; chaque vignette ouvre une vue en grand par ancre
 * (`:target`), un lightbox sobre entièrement en CSS.
 */
export function cssAccrochage(charte) {
    return `${fontFaces(charte)}

.achat { margin-block: 24px; }
.bouton-achat { display: inline-flex; align-items: center; min-height: 44px; padding: 12px 20px; border: 1px solid var(--ink); color: var(--ink); text-decoration: none; }
.bouton-achat:focus-visible { outline: 2px solid var(--ink); outline-offset: 4px; }

:root {
  --couleur-principale: ${charte.couleurPrincipale};
  --couleur-secondaire: ${charte.couleurSecondaire};
  --ground: #FFFFFF;
  --ink: var(--couleur-principale);
  --accent: var(--couleur-secondaire);
  --sur-accent: #FFFFFF;
  --muted: color-mix(in srgb, var(--ink) 58%, var(--ground));
  --faint: color-mix(in srgb, var(--ink) 38%, var(--ground));
  --hair: color-mix(in srgb, var(--ink) 12%, var(--ground));
  --plate: color-mix(in srgb, var(--ink) 9%, #EDEDEC);
  --titres: ${pile(charte.policeTitres)};
  --texte: ${pile(charte.policeTexte)};
  --marge: 28px;
  --blocs: ${String(charte.accueil.length)};
}

@media (prefers-color-scheme: dark) {
  :root:not([data-theme="light"]) {
    --ground: #0A0A0C;
    --ink: color-mix(in srgb, var(--couleur-principale) 12%, #F1F1EE);
    --sur-accent: #0A0A0C;
    --muted: color-mix(in srgb, var(--ink) 62%, var(--ground));
    --faint: color-mix(in srgb, var(--ink) 40%, var(--ground));
    --hair: color-mix(in srgb, var(--ink) 16%, var(--ground));
    --plate: color-mix(in srgb, var(--ink) 14%, #1C1C1F);
  }
}

*, *::before, *::after { box-sizing: border-box; }

html { background: var(--ground); }

body {
  margin: 0;
  background: var(--ground);
  color: var(--ink);
  font-family: var(--texte);
  font-size: 16px;
  line-height: 1.55;
  -webkit-font-smoothing: antialiased;
}

a { color: inherit; text-decoration: none; }
a:hover { color: var(--accent); }
a:focus-visible, input:focus-visible, textarea:focus-visible, button:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 3px;
}

img { max-width: 100%; height: auto; display: block; }
picture { display: contents; }

h1, h2, h3, h4 { font-family: var(--titres); font-weight: 400; letter-spacing: -0.01em; }
em { font-style: italic; }

.saut {
  position: absolute;
  left: -9999px;
  top: 0;
  background: var(--ground);
  color: var(--ink);
  padding: 12px 18px;
  z-index: 30;
}
.saut:focus { left: 0; }

.invisible {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}

/* Champ piège des formulaires publics : hors de l’écran, jamais rempli à la main. */
.piege {
  position: absolute;
  left: -10000px;
  width: 1px;
  height: 1px;
  overflow: hidden;
}

/* Boîte lisible : tout le reste court bord à bord. */
.boite { max-width: 1440px; margin: 0 auto; padding: 0 var(--marge); }

.label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--muted);
  margin: 0;
}

.lien-souligne { border-bottom: 1px solid currentColor; }
.sous-label { margin-top: 40px; padding-bottom: 14px; }

.entete { border-bottom: 1px solid var(--hair); }
.entete-int {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  padding: 18px var(--marge);
  flex-wrap: wrap;
}
.logo { font-family: var(--titres); font-size: 19px; font-weight: 600; letter-spacing: -0.01em; text-transform: uppercase; }
.logo img { display: inline-block; }
.entete nav { display: flex; gap: 20px; font-size: 13.5px; font-weight: 500; flex-wrap: wrap; }
.entete nav a { padding-bottom: 2px; border-bottom: 2px solid transparent; }
.entete nav a[aria-current="page"] { border-bottom-color: var(--accent); }
.entete .lang { color: var(--muted); letter-spacing: 0.08em; }

section { padding-top: 56px; }
section:first-of-type { padding-top: 0; }

.tete-section { display: flex; justify-content: space-between; align-items: baseline; gap: 24px; border-bottom: 2px solid var(--ink); padding-bottom: 10px; margin-bottom: 24px; }

.plate { background: var(--plate); width: 100%; }
.une-visuel img,
.tuile img,
.fiche-oeuvre .visuels img,
.mosaique img,
.portrait-plein img,
.vue-grand img { background: var(--plate); }

figure { margin: 0; }
figcaption { font-size: 12px; color: var(--muted); margin-top: 8px; }

/* Accueil : l'image d'abord, plein écran */
.une { padding-top: 0; }
.une-visuel { aspect-ratio: 16 / 10; max-height: 86vh; overflow: hidden; }
.une-visuel img { width: 100%; height: 100%; object-fit: cover; }
.une-cartel { padding: 28px var(--marge) 48px; max-width: 720px; }
.une-cartel h1 { font-size: clamp(28px, 5vw, 56px); line-height: 1.02; margin: 10px 0 6px; text-transform: uppercase; text-wrap: balance; }
.une-cartel .dates { font-size: 13.5px; color: var(--muted); margin: 0 0 16px; }
.une-cartel p.txt { font-size: 16px; margin: 0 0 22px; max-width: 56ch; }
.btn {
  display: inline-block;
  background: var(--ink);
  color: var(--ground);
  font-weight: 600;
  font-size: 12.5px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 13px 20px;
}
.btn:hover { background: var(--accent); color: var(--sur-accent); }

/* Grille plein écran, sans gouttière */
.grille { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1px; background: var(--hair); list-style: none; padding: 0; margin: 0; }
.tuile { background: var(--ground); }
.tuile a { display: block; aspect-ratio: 4 / 5; overflow: hidden; }
.tuile img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.4s ease; }
.tuile a:hover img { transform: scale(1.03); }
.legende { margin: 10px var(--marge) 28px; font-size: 13px; line-height: 1.4; }
.legende .a { display: block; font-weight: 600; }
.legende .t { display: block; color: var(--muted); }

/* Vue en grand : lightbox sobre en CSS, sans script (:target) */
.vue-grand {
  position: fixed;
  inset: 0;
  z-index: 40;
  display: flex;
  flex-direction: column;
  background: var(--ground);
  opacity: 0;
  pointer-events: none;
  overflow-y: auto;
  transition: opacity 0.15s ease;
}
.vue-grand:target { opacity: 1; pointer-events: auto; }
.vue-grand .fermer {
  position: sticky;
  top: 0;
  align-self: flex-end;
  z-index: 1;
  background: var(--ground);
  color: var(--ink);
  font-size: 28px;
  line-height: 1;
  padding: 16px var(--marge);
}
.vg-cadre { width: 100%; }
.vg-cadre img { width: 100%; height: auto; max-height: 78vh; object-fit: contain; margin: 0 auto; }
.vg-cartel { padding: 24px var(--marge) 56px; max-width: 640px; }
.vg-cartel dl { font-size: 14px; line-height: 1.6; margin: 0 0 18px; }
.vg-cartel dt { font-weight: 600; }
.vg-cartel dd { margin: 0 0 4px; color: var(--muted); }
.vg-cartel dd.prix { color: var(--ink); font-weight: 500; }
.vg-cartel dd.enq { font-size: 12px; letter-spacing: 0.08em; text-transform: uppercase; color: var(--accent); }
.vg-fiche { display: inline-block; font-size: 12.5px; letter-spacing: 0.06em; text-transform: uppercase; }

/* Index typographique des artistes */
.index { list-style: none; padding: 0; margin: 0; border-top: 1px solid var(--ink); }
.index li { border-bottom: 1px solid var(--hair); }
.index a { display: flex; justify-content: space-between; padding: 14px 0; font-family: var(--titres); font-size: clamp(19px, 2.6vw, 28px); text-transform: uppercase; letter-spacing: -0.015em; }

.rangs { list-style: none; padding: 0; margin: 0; border-top: 1px solid var(--ink); }
.rang { display: grid; grid-template-columns: 180px 1fr 200px; gap: 16px 28px; padding: 16px 0; border-bottom: 1px solid var(--hair); align-items: baseline; font-size: 13.5px; }
.rang .d { font-size: 11.5px; letter-spacing: 0.08em; text-transform: uppercase; color: var(--accent); font-weight: 600; }
.rang .t { font-family: var(--titres); font-size: 18px; text-transform: none; }
.rang .t span { display: block; font-family: var(--texte); font-size: 13px; color: var(--muted); margin-top: 3px; }
.rang .p { font-size: 12.5px; color: var(--muted); }

/* Pages de contenu */
.portrait-plein { aspect-ratio: 21 / 9; max-height: 60vh; overflow: hidden; }
.portrait-plein img { width: 100%; height: 100%; object-fit: cover; }

.page-tete { padding: 32px var(--marge) 8px; }
.page-tete h1 { font-size: 34px; margin: 10px 0 0; max-width: 24ch; text-transform: uppercase; text-wrap: balance; }
.page-tete .chapeau { font-size: 15px; color: var(--muted); margin: 14px 0 0; max-width: 56ch; }

.prose { max-width: 62ch; font-size: 15.5px; }
.prose p { margin: 0 0 1em; }

.bandeau { aspect-ratio: 21 / 8; max-height: 70vh; overflow: hidden; }
.bandeau img { width: 100%; height: 100%; object-fit: cover; }

.fiche-oeuvre { padding-top: 0; }
.fiche-oeuvre .visuels { display: grid; gap: 2px; }
.fiche-oeuvre .visuels img { width: 100%; max-height: 92vh; object-fit: contain; background: var(--plate); }
.fiche-oeuvre .cartel { padding: 28px var(--marge) 56px; max-width: 640px; }
.fiche-oeuvre .cartel h1 { font-size: 26px; margin: 8px 0 0; text-transform: none; }
.fiche-oeuvre .cartel .artiste { font-size: 15px; margin: 0; }
.fiche-oeuvre .cartel dl { margin: 22px 0 0; font-size: 14px; }
.fiche-oeuvre .cartel dt { font-size: 10.5px; letter-spacing: 0.12em; text-transform: uppercase; color: var(--muted); margin-top: 14px; }
.fiche-oeuvre .cartel dd { margin: 3px 0 0; }
.fiche-oeuvre .cartel .prose { margin-top: 26px; }
.fiche-oeuvre .cartel .formulaire { margin-top: 36px; }

.mosaique { display: grid; grid-template-columns: repeat(2, 1fr); gap: 1px; background: var(--hair); list-style: none; padding: 0; margin: 0; }
.mosaique img { width: 100%; }

/* Formulaires */
.formulaire { max-width: 480px; }
.champ { display: block; margin-bottom: 20px; }
.champ span { display: block; font-size: 10.5px; letter-spacing: 0.12em; text-transform: uppercase; color: var(--muted); margin-bottom: 6px; }
.champ input, .champ textarea {
  width: 100%;
  border: 0;
  border-bottom: 2px solid var(--ink);
  background: transparent;
  color: var(--ink);
  font: inherit;
  font-size: 15px;
  padding: 8px 0;
}
.champ textarea { min-height: 110px; resize: vertical; }
.formulaire button {
  display: inline-block;
  background: var(--ink);
  color: var(--ground);
  border: 0;
  font: inherit;
  font-weight: 600;
  font-size: 12.5px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 12px 18px;
  cursor: pointer;
}
.formulaire button:hover { background: var(--accent); color: var(--sur-accent); }
.formulaire [data-etat] { display: block; margin-top: 14px; font-size: 13px; color: var(--muted); }
.formulaire [data-etat][data-erreur="oui"] { color: var(--accent); }

.newsletter { margin-top: 56px; background: var(--plate); }
.newsletter .boite { padding-top: 40px; padding-bottom: 48px; }
.newsletter p { font-size: 14px; color: var(--muted); margin: 10px 0 18px; max-width: 48ch; }
.newsletter .ligne { display: flex; gap: 12px; border-bottom: 2px solid var(--ink); padding-bottom: 8px; max-width: 460px; }
.newsletter .ligne input { flex: 1; border: 0; background: transparent; color: var(--ink); font: inherit; font-size: 15px; padding: 0; }
.newsletter .ligne button { border: 0; background: transparent; color: var(--accent); font: inherit; font-weight: 600; font-size: 12.5px; letter-spacing: 0.06em; text-transform: uppercase; cursor: pointer; padding: 0; }

/* Plan du site */
.plan { display: grid; grid-template-columns: repeat(3, 1fr); gap: 44px 40px; padding-top: 32px; padding-bottom: 56px; }
.plan ul { list-style: none; padding: 0; margin: 12px 0 0; font-size: 14px; line-height: 1.9; }

/* Pied de page */
.pied { border-top: 1px solid var(--hair); margin-top: 72px; }
.pied-int { padding: 34px var(--marge) 44px; display: grid; grid-template-columns: 1.2fr repeat(4, 1fr); gap: 24px 32px; font-size: 13px; color: var(--muted); }
.pied-nom { font-family: var(--titres); font-size: 22px; text-transform: uppercase; color: var(--ink); letter-spacing: -0.01em; }
.pied b, .pied h2 { display: block; color: var(--ink); font-weight: 600; font-size: 10.5px; letter-spacing: 0.12em; text-transform: uppercase; margin: 0 0 10px; }
.pied ul { list-style: none; padding: 0; margin: 0; }
.pied address { font-style: normal; }
.pied address + address { margin-top: 10px; }
.mention { grid-column: 1 / -1; border-top: 1px solid var(--hair); padding-top: 18px; font-size: 11.5px; color: var(--faint); display: flex; flex-wrap: wrap; gap: 8px 26px; }

@media (max-width: 1000px) {
  .grille { grid-template-columns: repeat(2, 1fr); }
  .plan { grid-template-columns: repeat(2, 1fr); }
  .pied-int { grid-template-columns: 1fr 1fr; }
}

@media (max-width: 760px) {
  :root { --marge: 16px; }
  .une-cartel h1 { font-size: 34px; }
  .rang { grid-template-columns: 1fr; gap: 4px; }
  .mosaique { grid-template-columns: 1fr; }
  section { padding-top: 40px; }
}

@media (max-width: 520px) {
  .grille { grid-template-columns: 1fr; }
  .plan { grid-template-columns: 1fr; }
}

@media print {
  .entete nav, .saut, .newsletter, .formulaire, .vue-grand { display: none; }
}
`;
}
//# sourceMappingURL=css.js.map